import { type RefObject, useCallback, useLayoutEffect, useRef, useState } from "react";
import { isVehicleIslandHeadsignTruncated } from "./vehicleInfoChrome.tools";

interface UseVehicleIslandDelayStatusCompactParams {

    /** Signed delay in seconds; measurement is skipped when undefined. */
    "delaySec": number | undefined;

    /** Formatted headsign text; measurement is skipped when null. */
    "formattedHeadsign": string | null;

    /** Ref on the visible truncated headsign text node. */
    "headsignRef": RefObject<HTMLElement | null>;

    /** Ref on the island row; only its width changes trigger a re-test. */
    "rowRef": RefObject<HTMLElement | null>;
}

interface UseVehicleIslandDelayStatusCompactResult {

    /** When true, render abbreviated `"Ret."` / `"Av."` instead of full words. */
    "useCompactStatusLine": boolean;
}

/**
 * Chooses abbreviated vs full delay status words from headsign ellipsis.
 *
 * Starts with the full status word. When the headsign ellipsizes, switches to the
 * abbreviated word and keeps it until the island row width changes or content updates.
 *
 * @param params Measurement inputs.
 * @returns Compact flag for {@link resolveVehicleDelayBadgePresentation}.
 */
export function useVehicleIslandDelayStatusCompact( params: UseVehicleIslandDelayStatusCompactParams ): UseVehicleIslandDelayStatusCompactResult {
    const {
        delaySec,
        formattedHeadsign,
        headsignRef,
        rowRef,
    } = params;
    const compactLockedRef = useRef( false );
    const lastRowWidthPxRef = useRef( 0 );
    const [ measureGeneration, setMeasureGeneration ] = useState( 0 );
    const [ useCompactStatusLine, setUseCompactStatusLine ] = useState( false );
    const measurementEnabled = delaySec !== undefined && formattedHeadsign !== null;

    const evaluateFullStatusEllipsis = useCallback( (): void => {
        if ( compactLockedRef.current || !measurementEnabled ) {
            return;
        }

        const headsignElement = headsignRef.current;

        if ( headsignElement === null ) {
            return;
        }

        if ( !isVehicleIslandHeadsignTruncated( headsignElement ) ) {
            return;
        }

        compactLockedRef.current = true;
        setUseCompactStatusLine( true );
    }, [ headsignRef, measurementEnabled ] );

    const requestFullStatusMeasurement = useCallback( (): void => {
        if ( !measurementEnabled ) {
            return;
        }

        compactLockedRef.current = false;
        setMeasureGeneration( ( currentGeneration ) => currentGeneration + 1 );
        setUseCompactStatusLine( false );
    }, [ measurementEnabled ] );

    useLayoutEffect( (): ( () => void ) | void => {
        if ( !measurementEnabled || useCompactStatusLine ) {
            return;
        }

        const pendingAnimationFrame = window.requestAnimationFrame( () => {
            evaluateFullStatusEllipsis();
        } );

        return (): void => {
            window.cancelAnimationFrame( pendingAnimationFrame );
        };
    }, [
        evaluateFullStatusEllipsis,
        measureGeneration,
        measurementEnabled,
        useCompactStatusLine,
    ] );

    useLayoutEffect( (): ( () => void ) | void => {
        if ( !measurementEnabled ) {
            return;
        }

        const rowElement = rowRef.current;

        if ( rowElement === null ) {
            return;
        }

        let pendingAnimationFrame = 0;
        const scheduleRetest = (): void => {
            pendingAnimationFrame = window.requestAnimationFrame( () => {
                requestFullStatusMeasurement();
            } );
        };

        lastRowWidthPxRef.current = rowElement.clientWidth;
        scheduleRetest();

        if ( typeof ResizeObserver === "undefined" ) {
            return (): void => {
                window.cancelAnimationFrame( pendingAnimationFrame );
            };
        }

        const resizeObserver = new ResizeObserver( () => {
            const nextRowWidthPx = rowElement.clientWidth;

            if ( nextRowWidthPx === lastRowWidthPxRef.current ) {
                return;
            }

            lastRowWidthPxRef.current = nextRowWidthPx;
            scheduleRetest();
        } );

        resizeObserver.observe( rowElement );

        return (): void => {
            window.cancelAnimationFrame( pendingAnimationFrame );
            resizeObserver.disconnect();
        };
    }, [
        delaySec,
        formattedHeadsign,
        measurementEnabled,
        requestFullStatusMeasurement,
        rowRef,
    ] );

    return { "useCompactStatusLine": measurementEnabled ? useCompactStatusLine : true };
}
